import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import ResponsiveNavigation from '../components/ResponsiveNavigation';
import MobileFilters from '../components/MobileFilters';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { MapPin, Search, Star, MoreHorizontal, X, Mail, Phone, Calendar, Briefcase, DollarSign, Globe } from 'lucide-react';

const Sourcing = () => {
  const [filters, setFilters] = useState({
    nameSearch: '',
    position: '',
    skillset: '',
    experience: '',
    employmentType: [],
    salaryExpectations: '',
    workplace: [],
    languages: '',
    noticePeriod: '',
    city: '',
    range: ''
  });

  const [allCandidates, setAllCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [geoStatus, setGeoStatus] = useState(''); // '', 'geocoding', 'ready', 'no-match'
  const geoCache = useRef({}); // place -> {lat, lon} | null

  // Fetch real prospects from the backend
  useEffect(() => {
    const fetchProspects = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/prospects`);
        
        if (response.data.success) {
          // Store full prospect data for modal
          const formattedCandidates = response.data.data.map(prospect => ({
            ...prospect,
            id: prospect.id,
            name: `${prospect.firstName || ''} ${prospect.lastName || ''}`.trim() || 'Anonymous',
            jobTitle: prospect.currentJobTitle || prospect.jobTitle || '',
            company: prospect.currentCompany || '',
            skills: prospect.skills && prospect.skills.length > 0 ? prospect.skills : ['No skills listed'],
            location: formatLocation(prospect),
            salary: formatSalary(prospect),
            language: prospect.languages && prospect.languages.length > 0 ? prospect.languages.join(', ') : 'Not specified'
          }));
          
          setAllCandidates(formattedCandidates);
          setFilteredCandidates(formattedCandidates);
        }
      } catch (err) {
        console.error('Error fetching prospects:', err);
        setError('Failed to load prospects. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProspects();
    
    // Auto-refresh every 30 seconds to show new prospects
    const interval = setInterval(fetchProspects, 30000);
    return () => clearInterval(interval);
  }, []);

  // Format location based on prospect data
  const formatLocation = (prospect) => {
    const location = prospect.location || 'Location not specified';
    const workArrangement = prospect.workArrangement || prospect.remotePreference || '';
    return workArrangement ? `${location} (${workArrangement})` : location;
  };

  // Format salary based on prospect data
  const formatSalary = (prospect) => {
    if (prospect.expectedSalaryMin && prospect.expectedSalaryMax) {
      const currency = prospect.salaryCurrency || '€';
      return `${currency} ${prospect.expectedSalaryMin.toLocaleString()} - ${prospect.expectedSalaryMax.toLocaleString()}`;
    } else if (prospect.expectedSalaryMin) {
      const currency = prospect.salaryCurrency || '€';
      return `${currency} ${prospect.expectedSalaryMin.toLocaleString()}+`;
    }
    return 'Salary not specified';
  };

  // Geocoding helper (Nominatim — free, no key needed)
  const geocodePlace = async (place) => {
    if (!place) return null;
    const key = place.trim().toLowerCase();
    if (geoCache.current[key] !== undefined) return geoCache.current[key];
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(place)}`, {
        headers: { 'Accept': 'application/json' },
      });
      const data = await res.json();
      const result = (data && data[0]) ? { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) } : null;
      geoCache.current[key] = result;
      return result;
    } catch {
      geoCache.current[key] = null;
      return null;
    }
  };

  const haversineKm = (a, b) => {
    if (!a || !b) return Infinity;
    const toRad = (d) => (d * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lon - a.lon);
    const sa = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(sa));
  };

  // Apply filters whenever filters or candidates change
  useEffect(() => {
    applyFilters();
  }, [filters, allCandidates]);

  const applyFilters = async () => {
    let filtered = [...allCandidates];

    // Name search (live)
    if (filters.nameSearch.trim()) {
      const q = filters.nameSearch.toLowerCase();
      filtered = filtered.filter(c => c.name.toLowerCase().includes(q));
    }

    // Position (contains)
    if (filters.position) {
      const q = filters.position.toLowerCase();
      filtered = filtered.filter(c =>
        c.jobTitle?.toLowerCase().includes(q) ||
        c.currentJobTitle?.toLowerCase().includes(q) ||
        (c.preferredJobTitles && c.preferredJobTitles.some(t => t.toLowerCase().includes(q)))
      );
    }

    // Skillset (comma-separated tokens, ANY-match)
    if (filters.skillset) {
      const tokens = filters.skillset.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
      if (tokens.length > 0) {
        filtered = filtered.filter(c =>
          (c.skills || []).some(skill => tokens.some(tok => skill.toLowerCase().includes(tok)))
        );
      }
    }

    // Experience (bucket vs years OR exact level)
    if (filters.experience) {
      filtered = filtered.filter(c => {
        const level = (c.experienceLevel || '').toLowerCase();
        const years = c.experienceYears ?? null;
        if (filters.experience === 'junior') return level === 'junior' || (years != null && years < 2);
        if (filters.experience === 'mid')    return level === 'mid' || (years != null && years >= 2 && years < 5);
        if (filters.experience === 'senior') return level === 'senior' || (years != null && years >= 5 && years < 8);
        if (filters.experience === 'lead')   return level === 'lead' || level === 'principal' || (years != null && years >= 8);
        return true;
      });
    }

    // Employment Type
    if (filters.employmentType.length > 0) {
      filtered = filtered.filter(c => {
        const wt = Array.isArray(c.workType) ? c.workType.join(' ') : (c.workType || '');
        return filters.employmentType.some(t => wt.toLowerCase().includes(t.toLowerCase()));
      });
    }

    // Salary buckets — overlap with prospect's expected min/max
    if (filters.salaryExpectations) {
      const ranges = { '40-60': [40000, 60000], '60-80': [60000, 80000], '80-100': [80000, 100000], '100+': [100000, Infinity] };
      const [lo, hi] = ranges[filters.salaryExpectations] || [0, Infinity];
      filtered = filtered.filter(c => {
        const min = c.expectedSalaryMin ?? null;
        const max = c.expectedSalaryMax ?? min;
        if (min == null) return false;
        // Overlap test: max >= lo AND min <= hi
        return (max ?? min) >= lo && min <= hi;
      });
    }

    // Workplace
    if (filters.workplace.length > 0) {
      filtered = filtered.filter(c => {
        const wa = (c.workArrangement || c.remotePreference || '').toLowerCase();
        return filters.workplace.some(p => wa.includes(p.toLowerCase()));
      });
    }

    // Languages
    if (filters.languages) {
      const q = filters.languages.toLowerCase();
      filtered = filtered.filter(c =>
        (c.languages || []).some(l => l.toLowerCase().includes(q))
      );
    }

    // Notice Period — derived from `availability`
    if (filters.noticePeriod) {
      filtered = filtered.filter(c => {
        const av = (c.availability || '').toLowerCase();
        if (filters.noticePeriod === 'immediate') return av.includes('immediate') || av.includes('available now') || av.includes('0');
        if (filters.noticePeriod === '1-month')   return av.includes('1 month') || av.includes('30 day') || av.includes('one month');
        if (filters.noticePeriod === '2-months')  return av.includes('2 month') || av.includes('60 day') || av.includes('two month');
        if (filters.noticePeriod === '3-months')  return av.includes('3 month') || av.includes('90 day') || av.includes('three month');
        return true;
      });
    }

    // City + Range (km) via Nominatim geocoding (only when both set)
    if (filters.city && filters.range) {
      setGeoStatus('geocoding');
      const target = await geocodePlace(filters.city);
      const textQ = filters.city.toLowerCase();
      // Text fallback: candidates whose location string mentions the search term.
      const textMatches = filtered.filter(c => c.location?.toLowerCase().includes(textQ));
      let result = [];
      if (!target) {
        result = textMatches;
      } else {
        const maxKm = parseInt(filters.range, 10);
        const geoMatches = (await Promise.all(filtered.map(async (c) => {
          if (!c.location) return null;
          const coord = await geocodePlace(c.location);
          if (!coord) return null;
          return haversineKm(target, coord) <= maxKm ? c : null;
        }))).filter(Boolean);
        // Union geo-matches ∪ text-matches (dedupe by id).
        const seen = new Set();
        for (const c of [...geoMatches, ...textMatches]) {
          if (!seen.has(c.id)) { result.push(c); seen.add(c.id); }
        }
      }
      // "Always work" — if the city/range restriction removed everything, fall
      // back to the base filtered list (ignoring city/range) so the recruiter
      // still sees prospects and a clear hint about why the location filter
      // didn't narrow things.
      if (result.length === 0) {
        // keep pre-city/range list unchanged
        setGeoStatus('degraded');
      } else {
        filtered = result;
        setGeoStatus(!target ? 'text-fallback' : 'ready');
      }
    } else if (filters.city) {
      // Plain text city contains (no range chosen)
      const q = filters.city.toLowerCase();
      filtered = filtered.filter(c => c.location?.toLowerCase().includes(q));
      setGeoStatus('');
    } else {
      setGeoStatus('');
    }

    setFilteredCandidates(filtered);
  };

  const handleSearch = () => {
    applyFilters();
  };

  const handleResetFilters = () => {
    setFilters({
      nameSearch: '',
      position: '',
      skillset: '',
      experience: '',
      employmentType: [],
      salaryExpectations: '',
      workplace: [],
      languages: '',
      noticePeriod: '',
      city: '',
      range: ''
    });
    setGeoStatus('');
  };

  const handleProspectClick = (candidate) => {
    // Navigate to prospect detail page instead of opening modal
    window.location.href = `/sourcing/${candidate.id}`;
  };

  const toggleEmploymentType = (type) => {
    setFilters(prev => ({
      ...prev,
      employmentType: prev.employmentType.includes(type)
        ? prev.employmentType.filter(t => t !== type)
        : [...prev.employmentType, type]
    }));
  };

  const toggleWorkplace = (type) => {
    setFilters(prev => ({
      ...prev,
      workplace: prev.workplace.includes(type)
        ? prev.workplace.filter(t => t !== type)
        : [...prev.workplace, type]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ResponsiveNavigation />
      
      <div className="max-w-7xl mx-auto p-4 lg:p-6">
        <h1 className="text-xl lg:text-2xl font-semibold mb-4 lg:mb-6">Sourcing</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
          {/* Desktop Filters Sidebar */}
          <div className="hidden lg:block lg:col-span-3">
            <Card>
              <CardContent className="p-6 space-y-6">
                {/* Position */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Position</label>
                  <Input 
                    placeholder="Enter position" 
                    value={filters.position}
                    onChange={(e) => setFilters(prev => ({...prev, position: e.target.value}))}
                    className="w-full"
                    data-testid="filter-position"
                  />
                </div>

                {/* Skillset */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Skillset</label>
                  <Input 
                    placeholder="e.g. React, Python, SQL" 
                    value={filters.skillset}
                    onChange={(e) => setFilters(prev => ({...prev, skillset: e.target.value}))}
                    className="w-full"
                    data-testid="filter-skillset"
                  />
                </div>

                {/* Experience */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Experience</label>
                  <Select value={filters.experience} onValueChange={(value) => setFilters(prev => ({...prev, experience: value === '__any__' ? '' : value}))}>
                    <SelectTrigger data-testid="filter-experience">
                      <SelectValue placeholder="Select experience" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__any__">Any</SelectItem>
                      <SelectItem value="junior">Junior (0–2 years)</SelectItem>
                      <SelectItem value="mid">Mid (2–5 years)</SelectItem>
                      <SelectItem value="senior">Senior (5–8 years)</SelectItem>
                      <SelectItem value="lead">Lead / Principal (8+ years)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Employment Type */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Employment Type</label>
                  <div className="flex gap-2">
                    {['Full-time', 'Part-time'].map((type) => (
                      <Button
                        key={type}
                        onClick={() => toggleEmploymentType(type)}
                        variant={filters.employmentType.includes(type) ? "default" : "outline"}
                        size="sm"
                        className="text-xs"
                      >
                        {type}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Salary Expectations */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Salary Expectations, EUR</label>
                  <Select value={filters.salaryExpectations} onValueChange={(value) => setFilters(prev => ({...prev, salaryExpectations: value === '__any__' ? '' : value}))}>
                    <SelectTrigger data-testid="filter-salary">
                      <SelectValue placeholder="Select salary range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__any__">Any</SelectItem>
                      <SelectItem value="40-60">€40,000 - €60,000</SelectItem>
                      <SelectItem value="60-80">€60,000 - €80,000</SelectItem>
                      <SelectItem value="80-100">€80,000 - €100,000</SelectItem>
                      <SelectItem value="100+">€100,000+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Workplace */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Workplace</label>
                  <div className="flex gap-2">
                    {['On-site', 'Hybrid', 'Remote'].map((type) => (
                      <Button
                        key={type}
                        onClick={() => toggleWorkplace(type)}
                        variant={filters.workplace.includes(type) ? "default" : "outline"}
                        size="sm"
                        className="text-xs"
                      >
                        {type}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Languages */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Languages</label>
                  <Select value={filters.languages} onValueChange={(value) => setFilters(prev => ({...prev, languages: value === '__any__' ? '' : value}))}>
                    <SelectTrigger data-testid="filter-languages">
                      <SelectValue placeholder="Select languages" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__any__">Any</SelectItem>
                      <SelectItem value="english">English</SelectItem>
                      <SelectItem value="german">German</SelectItem>
                      <SelectItem value="spanish">Spanish</SelectItem>
                      <SelectItem value="french">French</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Notice Period — hidden until field is captured in onboarding
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Notice Period</label>
                  <Select value={filters.noticePeriod} onValueChange={(value) => setFilters(prev => ({...prev, noticePeriod: value === '__any__' ? '' : value}))}>
                    <SelectTrigger data-testid="filter-notice">
                      <SelectValue placeholder="Select notice period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__any__">Any</SelectItem>
                      <SelectItem value="immediate">Immediate</SelectItem>
                      <SelectItem value="1-month">1 Month</SelectItem>
                      <SelectItem value="2-months">2 Months</SelectItem>
                      <SelectItem value="3-months">3 Months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                */}
              </CardContent>
            </Card>
          </div>

          {/* Search Results */}
          <div className="lg:col-span-9">
            {/* Mobile Filters */}
            <MobileFilters 
              filters={filters}
              setFilters={setFilters}
              toggleEmploymentType={toggleEmploymentType}
              toggleWorkplace={toggleWorkplace}
            />

            {/* Search Bar — pill style (matches My Candidates aesthetic) */}
            <div className="mb-2">
              <div className="flex flex-col sm:flex-row items-stretch bg-white rounded-full shadow-sm border-2 border-gray-900/90 focus-within:border-blue-500 transition-colors overflow-hidden">
                <div className="flex items-center gap-2 flex-1 min-w-0 px-5 py-3.5 border-r border-gray-200">
                  <Search className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="Search by name..."
                    value={filters.nameSearch}
                    onChange={(e) => setFilters(prev => ({ ...prev, nameSearch: e.target.value }))}
                    className="flex-1 bg-transparent outline-none text-sm text-gray-900 placeholder:text-gray-400 min-w-0"
                    data-testid="sourcing-search-name"
                  />
                  {filters.nameSearch && (
                    <button type="button" onClick={() => setFilters(prev => ({ ...prev, nameSearch: '' }))} className="p-1 text-gray-400 hover:text-gray-600" aria-label="Clear name">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-1 min-w-0 px-5 py-3.5 border-r border-gray-200">
                  <MapPin className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="City"
                    value={filters.city}
                    onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value }))}
                    className="flex-1 bg-transparent outline-none text-sm text-gray-900 placeholder:text-gray-400 min-w-0"
                    data-testid="sourcing-search-city"
                  />
                  {filters.city && (
                    <button type="button" onClick={() => setFilters(prev => ({ ...prev, city: '' }))} className="p-1 text-gray-400 hover:text-gray-600" aria-label="Clear city">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 px-3 py-1.5">
                  <Select value={filters.range} onValueChange={(value) => setFilters(prev => ({ ...prev, range: value }))}>
                    <SelectTrigger className="border-0 shadow-none bg-transparent h-9 w-28 focus:ring-0 focus:ring-offset-0 text-sm" data-testid="sourcing-range">
                      <SelectValue placeholder="Range" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl shadow-lg border border-gray-200">
                      <SelectItem value="10">10 km</SelectItem>
                      <SelectItem value="25">25 km</SelectItem>
                      <SelectItem value="50">50 km</SelectItem>
                      <SelectItem value="100">100 km</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <button
                  onClick={handleResetFilters}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold text-sm px-8 py-3.5 sm:m-2 rounded-full transition-colors flex-shrink-0"
                  data-testid="sourcing-reset"
                >
                  Reset filters
                </button>
              </div>
            </div>
            {filters.city && filters.range && (
              <div className="text-xs text-gray-500 mb-4" data-testid="geo-status">
                {geoStatus === 'geocoding' && 'Geocoding locations…'}
                {geoStatus === 'degraded' && `No candidates near "${filters.city}" within ${filters.range} km — showing all matches instead`}
                {geoStatus === 'text-fallback' && `Showing candidates with "${filters.city}" in their location`}
                {geoStatus === 'ready' && `Within ${filters.range} km of ${filters.city}`}
              </div>
            )}
            {!(filters.city && filters.range) && <div className="mb-4" />}

            {/* Candidates List */}
            <div className="space-y-4">
              {loading ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <p className="text-gray-600">Loading prospects...</p>
                  </CardContent>
                </Card>
              ) : error ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <p className="text-red-600">{error}</p>
                  </CardContent>
                </Card>
              ) : filteredCandidates.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <p className="text-gray-600">
                      {allCandidates.length === 0 
                        ? 'No active prospects found. Prospects will appear here when they enable scouting mode.'
                        : 'No prospects match your filters. Try adjusting your search criteria.'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                filteredCandidates.map((candidate) => (
                  <Card 
                    key={candidate.id} 
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handleProspectClick(candidate)}
                  >
                    <CardContent className="p-4 lg:p-6">
                      <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-blue-600 font-semibold text-lg">
                              {candidate.name.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-blue-600 mb-1 truncate">{candidate.name}</h3>
                            {candidate.jobTitle && <p className="text-sm font-medium text-gray-800 truncate">{candidate.jobTitle}</p>}
                            {candidate.company && <p className="text-sm text-gray-500 mb-2 truncate">{candidate.company}</p>}
                            <div className="flex flex-wrap gap-1 mb-2">
                              {candidate.skills.map((skill, idx) => (
                                <Badge key={`${skill}-${idx}`} variant="secondary" className="text-xs">
                                  {skill}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex sm:flex-col items-end justify-between w-full sm:w-auto gap-4">
                          <div className="flex items-center gap-2 sm:mb-4 order-2 sm:order-1">
                            <button className="text-gray-400 hover:text-gray-600 p-1">
                              <MoreHorizontal className="w-4 h-4" />
                            </button>
                            <button className="text-gray-400 hover:text-yellow-500 p-1">
                              <Star className="w-4 h-4" />
                            </button>
                            <button className="text-gray-400 hover:text-blue-600 p-1">
                              <Search className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="text-sm text-gray-600 text-left sm:text-right order-1 sm:order-2">
                            <div className="flex items-center gap-1 mb-1">
                              <MapPin className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{candidate.location}</span>
                            </div>
                            <div className="font-semibold text-gray-900">{candidate.salary}</div>
                            <div className="text-xs text-gray-500">{candidate.language}</div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal removed - now using full page at /sourcing/:id */}
      {false && showModal && selectedProspect && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                {selectedProspect.firstName} {selectedProspect.lastName}
              </h2>
              <button 
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-semibold text-2xl">
                    {selectedProspect.firstName?.[0]}{selectedProspect.lastName?.[0]}
                  </span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {selectedProspect.currentJobTitle || selectedProspect.jobTitle}
                  </h3>
                  <div className="space-y-2 text-gray-600">
                    {selectedProspect.currentCompany && (
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        <span>{selectedProspect.currentCompany}</span>
                      </div>
                    )}
                    {selectedProspect.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{selectedProspect.location}</span>
                      </div>
                    )}
                    {selectedProspect.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        <span>{selectedProspect.email}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Bio */}
              {selectedProspect.bio && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">About</h4>
                  <p className="text-gray-600">{selectedProspect.bio}</p>
                </div>
              )}

              {/* Skills */}
              {selectedProspect.skills && selectedProspect.skills.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProspect.skills.map((skill, idx) => (
                      <Badge key={idx} variant="secondary">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Experience */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedProspect.experienceYears && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">Experience</h4>
                    <p className="text-gray-600">{selectedProspect.experienceYears} years</p>
                  </div>
                )}
                {selectedProspect.experienceLevel && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">Level</h4>
                    <p className="text-gray-600 capitalize">{selectedProspect.experienceLevel}</p>
                  </div>
                )}
              </div>

              {/* Work Preferences */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Work Preferences</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedProspect.workArrangement && (
                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-1">Work Arrangement</h5>
                      <p className="text-gray-600 capitalize">{selectedProspect.workArrangement}</p>
                    </div>
                  )}
                  {selectedProspect.workType && (
                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-1">Work Type</h5>
                      <p className="text-gray-600 capitalize">{selectedProspect.workType}</p>
                    </div>
                  )}
                  {selectedProspect.availability && (
                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-1">Availability</h5>
                      <p className="text-gray-600 capitalize">{selectedProspect.availability}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Salary Expectations */}
              {(selectedProspect.expectedSalaryMin || selectedProspect.expectedSalaryMax) && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Salary Expectations</h4>
                  <div className="flex items-center gap-2 text-gray-600">
                    <DollarSign className="w-4 h-4" />
                    <span>{formatSalary(selectedProspect)}</span>
                  </div>
                </div>
              )}

              {/* Languages */}
              {selectedProspect.languages && selectedProspect.languages.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Languages</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProspect.languages.map((lang, idx) => (
                      <Badge key={idx} variant="outline">
                        {lang}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Preferred Job Titles */}
              {selectedProspect.preferredJobTitles && selectedProspect.preferredJobTitles.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Interested In</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProspect.preferredJobTitles.map((title, idx) => (
                      <Badge key={idx} variant="secondary">
                        {title}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Experience Summary */}
              {selectedProspect.experienceSummary && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Experience Summary</h4>
                  <p className="text-gray-600 whitespace-pre-wrap">{selectedProspect.experienceSummary}</p>
                </div>
              )}

              {/* Resume */}
              {selectedProspect.resumeUrl && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Resume</h4>
                  <a 
                    href={selectedProspect.resumeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline flex items-center gap-2"
                  >
                    <Globe className="w-4 h-4" />
                    View Resume ({selectedProspect.resumeFilename || 'Download'})
                  </a>
                </div>
              )}

              {/* Documents */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Documents</h4>
                <div className="space-y-3">
                  {selectedProspect.resumeUrl && (
                    <a 
                      href={selectedProspect.resumeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-md hover:border-blue-300 hover:bg-blue-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                          <Globe className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Resume / CV</p>
                          <p className="text-xs text-gray-500">Click to view</p>
                        </div>
                      </div>
                      <span className="text-blue-600 text-sm font-medium">View →</span>
                    </a>
                  )}
                  
                  {selectedProspect.diplomaUrl && (
                    <a 
                      href={selectedProspect.diplomaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-md hover:border-blue-300 hover:bg-blue-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded flex items-center justify-center">
                          <Globe className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Diploma / Certificate</p>
                          <p className="text-xs text-gray-500">Click to view</p>
                        </div>
                      </div>
                      <span className="text-blue-600 text-sm font-medium">View →</span>
                    </a>
                  )}

                  {!selectedProspect.resumeUrl && !selectedProspect.diplomaUrl && (
                    <p className="text-gray-500 text-sm text-center py-4">No documents uploaded</p>
                  )}
                </div>
              </div>

              {/* Contact Information Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-3">Contact Information</h4>
                <div className="space-y-2 text-sm">
                  {selectedProspect.email && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <Mail className="w-4 h-4 text-blue-600" />
                      <span>{selectedProspect.email}</span>
                    </div>
                  )}
                  {selectedProspect.phone && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <Phone className="w-4 h-4 text-blue-600" />
                      <span>{selectedProspect.phone}</span>
                    </div>
                  )}
                  {selectedProspect.location && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <MapPin className="w-4 h-4 text-blue-600" />
                      <span>{selectedProspect.location}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                <button 
                  onClick={() => {
                    // TODO: Implement present position functionality
                    alert('Present Position feature - coming soon!');
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-md font-medium transition-colors"
                >
                  Present Position
                </button>
                <button 
                  onClick={() => {
                    window.location.href = `mailto:${selectedProspect.email}`;
                  }}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-md font-medium transition-colors"
                >
                  Contact via Email
                </button>
                <button 
                  onClick={() => {
                    // TODO: Implement save to candidates
                    alert('Save to My Candidates - coming soon!');
                  }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 px-4 rounded-md font-medium transition-colors"
                >
                  Save to Candidates
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sourcing;